import React, {useEffect, useState} from "react";
import {Box, Button, Link, Stack, Typography} from "@mui/material";
import {alpha} from "@mui/material/styles";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import {EmpTimeClockRestControllerApiFactory, TimeClockStatusDto} from "arteco-api-client-ts";
import {Link as RouterLink} from "react-router-dom";
import {useApiManager} from "../../../components/utils/api";
import {hhmm} from "./timeclock_util";
import {btnSx} from "../../../components/kit";
import {COLOR_AZUL_OSCURO, COLOR_ROJO_CLARO, COLOR_VERDE_ARTECO} from "../../../components/layout/colors";

/**
 * Fichar donde ya se está (#13709): al abrir el portal, si hoy toca trabajar y no hay entrada, lo
 * primero que se ve es el botón de entrada; si la jornada está abierta, el de salida. Con la jornada
 * ya cerrada, o en un día no laborable sin fichajes, no enseña nada.
 */
export const TimeClockBanner = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(EmpTimeClockRestControllerApiFactory);
	const [status, setStatus] = useState<TimeClockStatusDto>();
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		apiManager.executeRaw(ctrl.statusTimeClock(), undefined, true)
			.then(r => setStatus(r.data))
			.catch(() => undefined);
	}, []);

	if (!status) {
		return null;
	}
	const entries = status.todayEntries || [];
	const showIn = !status.open && entries.length === 0 && status.workingDay;
	if (!status.open && !showIn) {
		return null;
	}

	const clock = () => {
		setBusy(true);
		apiManager.execute(status.open ? ctrl.clockOut() : ctrl.clockIn(),
			status.open ? "Salida registrada" : "Entrada registrada")
			.then(setStatus)
			.catch(() => undefined)
			.finally(() => setBusy(false));
	};

	const open = !!status.open;
	return (
		<Box data-testid="timeclock-banner" sx={{
			p: {xs: 2.5, sm: 3}, borderRadius: 3, color: "#fff",
			background: `linear-gradient(135deg, ${COLOR_AZUL_OSCURO} 0%, #2f4a6e 100%)`,
		}}>
			<Stack direction={{xs: "column", sm: "row"}} spacing={2} alignItems={{sm: "center"}} justifyContent="space-between">
				<Stack direction="row" spacing={1.5} alignItems="center">
					<Box sx={{width: 10, height: 10, borderRadius: "50%", flexShrink: 0, bgcolor: open ? COLOR_VERDE_ARTECO : alpha("#fff", 0.5)}}/>
					<Box>
						<Typography variant="subtitle1" fontWeight={700}>
							{open ? `En jornada desde las ${hhmm(entries.find(e => e.status === "OPEN")?.startAt)}` : "Hoy aún no has fichado la entrada"}
						</Typography>
						<Link component={RouterLink} to="/app/employee/timeclock" underline="hover" sx={{color: alpha("#fff", 0.75), fontSize: 14}}>
							Ver registro de jornada
						</Link>
					</Box>
				</Stack>
				<Button variant="contained" disableElevation disabled={busy} onClick={clock}
						startIcon={open ? <LogoutRoundedIcon/> : <LoginRoundedIcon/>}
						sx={{
							...btnSx, py: 1.25, px: 3, fontSize: "1rem", whiteSpace: "nowrap",
							bgcolor: open ? COLOR_ROJO_CLARO : COLOR_VERDE_ARTECO, color: open ? "#fff" : COLOR_AZUL_OSCURO,
							"&:hover": {bgcolor: open ? "#e2504e" : "#22c9a8"},
						}}>
					{open ? "Fichar salida" : "Fichar entrada"}
				</Button>
			</Stack>
		</Box>
	);
};
